import React, { useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import { useApp } from "../../../context/AppState";
import { usePopup } from "../../../hooks/usePopup";
import useOfferProducts from "../../../hooks/useOfferProducts";
import {
  offerTypeUsesRuntimeResolution,
  PRODUCT_TO_OFFER,
} from "../../../constant/constant";
import { CatalogProductShell } from "../../../components/Preview/catalog-product";
import CatalogProductOfferSkeleton from "../../../components/Preview/catalog-product/CatalogProductOfferSkeleton";
import {
  isPopupRuntimeContentLoading,
  resolveRuntimeProductSlotCount,
} from "../../../utils/popup-runtime-content-loading";
import { anyProductHasSelectableVariants } from "../utils/product-variant-utils";
import styles from "./ProductRecommendationSlider.module.css";

function NavigationButton({ direction, disabled, onClick }) {
  const isPrev = direction === "prev";
  return (
    <button
      type="button"
      className={`${styles.navBtn} ${isPrev ? styles.navPrev : styles.navNext}`}
      disabled={disabled}
      onClick={onClick}
      aria-label={isPrev ? "Previous product" : "Next product"}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d={isPrev ? "M15 18l-6-6 6-6" : "M9 18l6-6-6-6"} />
      </svg>
    </button>
  );
}

export default function ProductRecommendationSlider({
  section,
  popupData,
  popupState,
  setPopupState,
  activeStep,
  handleClose,
  isPreviewMode,
  isMobile: isMobileProp,
  selectedSubType,
  selectedSubIndex,
  onSubBlockClick,
  onProductsChange,
  contentLoading = false,
}) {
  const { isMobile: viewportIsMobile } = useApp();
  const isMobile =
    typeof isMobileProp === "boolean" ? isMobileProp : viewportIsMobile;
  const { isLoadingProducts, fetchRandomProduct } = usePopup();
  const swiperRef = useRef(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  const { products, isResolvingOfferProducts } = useOfferProducts({
    section,
    popupData,
    activeStep,
    isPreviewMode,
    isMobile,
    onProductsChange,
    fetchOfferProducts: fetchRandomProduct,
  });

  const productsToOfferType =
    popupData?.generalSettings?.condition?.product?.products_to_offer?.type;
  const usesRuntimeOffer = offerTypeUsesRuntimeResolution(productsToOfferType);

  const count = products?.length || 0;
  const showVariantPickerRow = anyProductHasSelectableVariants(products);
  const useDesktopGrid = !isMobile && count > 1 && count <= 3;
  const useMobileTwinGrid = isMobile && count === 2;
  const useSwiper = !useDesktopGrid && !useMobileTwinGrid && count > 1;
  const desktopSlidesPerView = Math.min(3, count);
  const slidesPerView = isMobile
    ? count >= 2
      ? 2.12
      : 1
    : desktopSlidesPerView;
  const showNav = useSwiper && (isMobile ? count > 1 : count > desktopSlidesPerView);
  const mobileCardDensity = useMobileTwinGrid || (isMobile && useSwiper) ? "twin" : "single";

  const CardComponent = CatalogProductShell;

  const updateSwiperState = (swiper) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  };

  const cardProps = {
    section,
    popupData,
    popupState,
    setPopupState,
    activeStep,
    handleClose,
    isMobile,
    isPreviewMode,
    selectedSubType,
    selectedSubIndex,
    onSubBlockClick,
    showVariantPickerRow,
    mobileCardDensity,
  };

  const showRuntimeSkeleton = useMemo(() => {
    const runtimeLoading =
      contentLoading ||
      (!isPreviewMode && isPopupRuntimeContentLoading(popupData));
    const awaitingRuntimeProducts =
      !isPreviewMode && usesRuntimeOffer && count === 0;
    const awaitingSpecificProducts =
      !isPreviewMode &&
      productsToOfferType === PRODUCT_TO_OFFER.SPECIFIC_PRODUCT &&
      isResolvingOfferProducts;

    if (isPreviewMode) {
      return (
        (isLoadingProducts || isResolvingOfferProducts) && count === 0
      );
    }

    return (
      isLoadingProducts ||
      isResolvingOfferProducts ||
      runtimeLoading ||
      awaitingRuntimeProducts ||
      awaitingSpecificProducts
    );
  }, [
    contentLoading,
    count,
    isLoadingProducts,
    isPreviewMode,
    isResolvingOfferProducts,
    popupData,
    productsToOfferType,
    usesRuntimeOffer,
  ]);
  const skeletonCount = resolveRuntimeProductSlotCount(
    popupData,
    activeStep,
    section
  );

  if (showRuntimeSkeleton) {
    return (
      <CatalogProductOfferSkeleton
        section={section}
        popupData={popupData}
        isMobile={isMobile}
        count={skeletonCount}
        layoutProfile="recommendation"
      />
    );
  }

  if (!count) {
    return null;
  }

  if (useDesktopGrid || useMobileTwinGrid) {
    const gridClass = useMobileTwinGrid
      ? `${styles.gridRow} ${styles.gridRowTwo} ${styles.mobileTwinGrid}`
      : count === 2
        ? `${styles.gridRow} ${styles.gridRowTwo}`
        : styles.gridRow;
    return (
      <div className={`${styles.sliderWrap} ${styles.sliderWrapGrid}`}>
        <div className={gridClass}>
          {products.map((productData, productIndex) => (
            <div key={productData?.id || productIndex} className={styles.gridCol}>
              <CardComponent
                {...cardProps}
                productData={productData}
                productIndex={productIndex}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!useSwiper) {
    return (
      <div
        className={`${styles.singleCard} ${
          isMobile ? styles.singleCardMobile : ""
        }`.trim()}
      >
        <CardComponent
          {...cardProps}
          productData={products[0]}
          productIndex={0}
        />
      </div>
    );
  }

  return (
    <div className={styles.sliderWrap}>
      {showNav ? (
        <>
          <NavigationButton
            direction="prev"
            disabled={isBeginning}
            onClick={() => swiperRef.current?.slidePrev()}
          />
          <NavigationButton
            direction="next"
            disabled={isEnd}
            onClick={() => swiperRef.current?.slideNext()}
          />
        </>
      ) : null}
      <div className={styles.sliderInner}>
        <Swiper
          modules={[Navigation]}
          spaceBetween={isMobile ? 8 : 12}
          slidesPerView={slidesPerView}
          centeredSlides={false}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
            updateSwiperState(swiper);
          }}
          onSlideChange={updateSwiperState}
        >
          {products.map((productData, productIndex) => (
            <SwiperSlide key={productData?.id || productIndex}>
              <CardComponent
                {...cardProps}
                productData={productData}
                productIndex={productIndex}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
}

ProductRecommendationSlider.propTypes = {
  section: PropTypes.object,
  popupData: PropTypes.object,
  popupState: PropTypes.object,
  setPopupState: PropTypes.func,
  activeStep: PropTypes.number,
  handleClose: PropTypes.func,
  isPreviewMode: PropTypes.bool,
  isMobile: PropTypes.bool,
  selectedSubType: PropTypes.string,
  selectedSubIndex: PropTypes.number,
  onSubBlockClick: PropTypes.func,
  onProductsChange: PropTypes.func,
  contentLoading: PropTypes.bool,
};
